/**
 * InlineTextField Component (T103)
 * Editable text input for inline editing
 * 
 * Features:
 * - Text input with auto-focus
 * - Save/cancel buttons
 * - Enter key to save
 * - Escape key to cancel
 * - Loading state during save
 * - Keyboard accessible
 * 
 * @example
 * <InlineTextField
 *   value={currentValue}
 *   onChange={handleChange}
 *   onSave={handleSave}
 *   onCancel={handleCancel}
 * />
 */

import { useEffect, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/lib/utils';

type InlineTextFieldProps = {
  /** Current input value */
  value: string;
  
  /** Callback when value changes */
  onChange: (value: string) => void;
  
  /** Callback when save is triggered */
  onSave: () => void | Promise<void>;
  
  /** Callback when cancel is triggered */
  onCancel: () => void;
  
  /** Optional placeholder text */
  placeholder?: string;
  
  /** Optional CSS class */
  className?: string;
};

/**
 * InlineTextField Component
 * 
 * Single-line text input with inline save/cancel actions
 * Supports Enter to save, Escape to cancel
 * Auto-focuses on mount for immediate editing
 */
export function InlineTextField({
  value,
  onChange,
  onSave,
  onCancel,
  placeholder,
  className,
}: InlineTextFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Auto-focus on mount
  useEffect(() => {
    inputRef.current?.focus();
    // Select all text for easy replacement
    inputRef.current?.select();
  }, []);

  // Handle Enter key (save)
  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  // Handle save with loading state
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Text Input */}
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={isSaving}
        className="h-8 flex-1"
      />

      {/* Save Button */}
      <button
        type="button"
        onClick={handleSave}
        disabled={isSaving}
        className="inline-flex h-8 w-8 items-center justify-center rounded bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Save changes"
      >
        {isSaving ? (
          <span className="text-xs">...</span>
        ) : (
          <Check className="h-4 w-4" />
        )}
      </button>

      {/* Cancel Button */}
      <button
        type="button"
        onClick={onCancel}
        disabled={isSaving}
        className="inline-flex h-8 w-8 items-center justify-center rounded bg-gray-600 text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Cancel editing"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

